import * as THREE from "three";

export const GLOBE_RADIUS = 2;

// Convert lat/lon (degrees) to a THREE.Vector3 on a sphere of given radius.
// Aligned with an equirectangular texture mapped onto THREE.SphereGeometry.
export function latLonToVector3(lat, lon, radius = GLOBE_RADIUS) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -radius * Math.sin(phi) * Math.cos(theta);
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);
  return new THREE.Vector3(x, y, z);
}

// Inverse: sphere point -> {lat, lon}
export function vector3ToLatLon(vec, radius = GLOBE_RADIUS) {
  const v = vec.clone().normalize().multiplyScalar(radius);
  const phi = Math.acos(THREE.MathUtils.clamp(v.y / radius, -1, 1));
  const lat = 90 - phi * (180 / Math.PI);
  let theta = Math.atan2(v.z, -v.x);
  let lon = theta * (180 / Math.PI) - 180;
  while (lon < -180) lon += 360;
  while (lon > 180) lon -= 360;
  return { lat, lon };
}

// Tangent basis (T1, T2) at a unit point on the sphere.
export function tangentBasis(unit) {
  const up = Math.abs(unit.y) > 0.99 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  const t1 = new THREE.Vector3().crossVectors(up, unit).normalize();
  const t2 = new THREE.Vector3().crossVectors(unit, t1).normalize();
  return { t1, t2 };
}

// --- Seismic science helpers ---

// Energy release in Joules from moment magnitude (Gutenberg-Richter energy relation)
export function energyJoules(mw) {
  return Math.pow(10, 1.5 * mw + 4.8);
}

// TNT equivalent (kilotons) from joules
export function tntKilotons(joules) {
  return joules / 4.184e12;
}

// Peak Ground Acceleration proxy (% g) scaled with magnitude & soil
export function estimatePGA(mw, soilMultiplier = 1) {
  const base = Math.pow(10, 0.3 * (mw - 5)) * 12; // %g rough proxy
  return base * soilMultiplier;
}

export const SOIL_TYPES = [
  { id: "bedrock", label: "Bedrock", multiplier: 0.6 },
  { id: "dense_sand", label: "Dense Sand", multiplier: 1.0 },
  { id: "alluvium", label: "Saturated Alluvium", multiplier: 1.8 },
  { id: "soft_mud", label: "Soft Mud", multiplier: 2.6 },
];

export function soilMultiplier(id) {
  return SOIL_TYPES.find((s) => s.id === id)?.multiplier ?? 1;
}

export function faultType(mw) {
  if (mw >= 8.3) return "Megathrust";
  if (mw >= 7.0) return "Thrust";
  if (mw >= 6.0) return "Strike-Slip";
  return "Normal";
}

// Angular spread (radians on the globe) for a given magnitude
export function magnitudeToSpread(mw) {
  return Math.max(0.2, THREE.MathUtils.mapLinear(mw, 4, 9.5, 0.35, 2.4));
}

// Active particle count scaled with magnitude
export function magnitudeToParticleCount(mw, max) {
  const c = Math.floor(THREE.MathUtils.mapLinear(mw, 4, 9.5, 3000, 16000));
  return Math.min(max, Math.max(1500, c));
}

// Total energy release incorporating both magnitude and rupture duration.
export function energyWithDuration(mw, durationSec) {
  return energyJoules(mw) * (0.5 + durationSec / 60);
}

// Major population centers (lat, lon) for aerial cluster glows.
export const POP_CENTERS = [
  { name: "Tokyo", lat: 35.68, lon: 139.69 }, { name: "Delhi", lat: 28.61, lon: 77.21 },
  { name: "Shanghai", lat: 31.23, lon: 121.47 }, { name: "São Paulo", lat: -23.55, lon: -46.63 },
  { name: "Mexico City", lat: 19.43, lon: -99.13 }, { name: "Cairo", lat: 30.04, lon: 31.24 },
  { name: "Mumbai", lat: 19.08, lon: 72.88 }, { name: "Beijing", lat: 39.9, lon: 116.4 },
  { name: "Dhaka", lat: 23.81, lon: 90.41 }, { name: "Osaka", lat: 34.69, lon: 135.5 },
  { name: "New York", lat: 40.71, lon: -74.01 }, { name: "Karachi", lat: 24.86, lon: 67.0 },
  { name: "Buenos Aires", lat: -34.6, lon: -58.38 }, { name: "Istanbul", lat: 41.01, lon: 28.98 },
  { name: "Kolkata", lat: 22.57, lon: 88.36 }, { name: "Manila", lat: 14.6, lon: 120.98 },
  { name: "Los Angeles", lat: 34.05, lon: -118.24 }, { name: "Moscow", lat: 55.75, lon: 37.62 },
  { name: "Jakarta", lat: -6.21, lon: 106.85 }, { name: "Lagos", lat: 6.52, lon: 3.37 },
  { name: "London", lat: 51.51, lon: -0.13 }, { name: "Lima", lat: -12.05, lon: -77.04 },
  { name: "Bangkok", lat: 13.76, lon: 100.5 }, { name: "Tehran", lat: 35.69, lon: 51.39 },
  { name: "Bogotá", lat: 4.71, lon: -74.07 }, { name: "Chennai", lat: 13.08, lon: 80.27 },
  { name: "Santiago", lat: -33.45, lon: -70.67 }, { name: "Seoul", lat: 37.57, lon: 126.98 },
  { name: "San Francisco", lat: 37.77, lon: -122.42 }, { name: "Lisbon", lat: 38.72, lon: -9.14 },
];

export function tsunamiVelocityKmh(depthMeters = 4000) {
  // shallow water wave speed v = sqrt(g*d) -> km/h
  const g = 9.81;
  const v = Math.sqrt(g * depthMeters); // m/s
  return v * 3.6;
}

// ---- Ocean / land mask sampling from earth-water.png ----
let _maskCtx = null;
let _maskW = 0;
let _maskH = 0;
let _waterBright = true; // polarity: is water the bright value?
let _maskReady = false;

export function loadOceanMask(src = "/data/earth-water.png") {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      _maskW = img.width;
      _maskH = img.height;
      canvas.width = _maskW;
      canvas.height = _maskH;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      _maskCtx = ctx;
      // Calibrate polarity using a known ocean point (Pacific) & land point (Sahara)
      const ocean = _brightnessAt(0, -150);
      const land = _brightnessAt(23, 12);
      _waterBright = ocean >= land;
      _maskReady = true;
      resolve(true);
    };
    img.onerror = () => {
      _maskReady = false;
      resolve(false);
    };
    img.src = src;
  });
}

function _brightnessAt(lat, lon) {
  if (!_maskCtx) return 0;
  const u = (lon + 180) / 360;
  const v = (90 - lat) / 180;
  const x = Math.min(_maskW - 1, Math.max(0, Math.floor(u * _maskW)));
  const y = Math.min(_maskH - 1, Math.max(0, Math.floor(v * _maskH)));
  const d = _maskCtx.getImageData(x, y, 1, 1).data;
  return (d[0] + d[1] + d[2]) / 3;
}

export function isOffshore(lat, lon) {
  if (!_maskReady) return false;
  const b = _brightnessAt(lat, lon);
  const isWater = _waterBright ? b > 127 : b < 127;
  return isWater;
}

// ---- Volcanoes (100 major active volcanoes worldwide) ----
export const VOLCANOES = [
  { name: "Vesuvius", lat: 40.82, lon: 14.43 }, { name: "Etna", lat: 37.75, lon: 14.99 },
  { name: "Santorini", lat: 36.4, lon: 25.4 }, { name: "Eyjafjallajökull", lat: 63.63, lon: -19.62 },
  { name: "Mt. Fuji", lat: 35.36, lon: 138.73 }, { name: "Sakurajima", lat: 31.59, lon: 130.66 },
  { name: "Krakatoa", lat: -6.1, lon: 105.42 }, { name: "Merapi", lat: -7.54, lon: 110.44 },
  { name: "Pinatubo", lat: 15.13, lon: 120.35 }, { name: "Taal", lat: 14.0, lon: 120.99 },
  { name: "Kilauea", lat: 19.42, lon: -155.29 }, { name: "Mt. St. Helens", lat: 46.19, lon: -122.19 },
  { name: "Mt. Rainier", lat: 46.85, lon: -121.76 }, { name: "Yellowstone", lat: 44.43, lon: -110.67 },
  { name: "Popocatépetl", lat: 19.02, lon: -98.62 }, { name: "Cotopaxi", lat: -0.68, lon: -78.44 },
  { name: "Villarrica", lat: -39.42, lon: -71.93 }, { name: "Nyiragongo", lat: -1.52, lon: 29.25 },
  { name: "Erebus", lat: -77.53, lon: 167.17 }, { name: "Ruapehu", lat: -39.28, lon: 175.57 },
  { name: "Mauna Loa", lat: 19.48, lon: -155.6 }, { name: "Redoubt", lat: 60.49, lon: -152.74 },
  { name: "Shishaldin", lat: 54.76, lon: -163.97 }, { name: "Katmai", lat: 58.28, lon: -154.96 },
  { name: "Colima", lat: 19.51, lon: -103.62 }, { name: "El Chichón", lat: 17.36, lon: -93.23 },
  { name: "Fuego", lat: 14.47, lon: -90.88 }, { name: "Pacaya", lat: 14.38, lon: -90.6 },
  { name: "Santa María", lat: 14.76, lon: -91.55 }, { name: "Arenal", lat: 10.46, lon: -84.7 },
  { name: "Poás", lat: 10.2, lon: -84.23 }, { name: "Masaya", lat: 11.98, lon: -86.16 },
  { name: "Cerro Negro", lat: 12.51, lon: -86.7 }, { name: "Galeras", lat: 1.22, lon: -77.36 },
  { name: "Nevado del Ruiz", lat: 4.89, lon: -75.32 }, { name: "Sangay", lat: -2.0, lon: -78.34 },
  { name: "Ubinas", lat: -16.36, lon: -70.9 }, { name: "Misti", lat: -16.29, lon: -71.41 },
  { name: "Láscar", lat: -23.37, lon: -67.73 }, { name: "Chaitén", lat: -42.83, lon: -72.65 },
  { name: "Puyehue", lat: -40.59, lon: -72.12 }, { name: "Calbuco", lat: -41.33, lon: -72.61 },
  { name: "Hekla", lat: 63.98, lon: -19.7 }, { name: "Katla", lat: 63.63, lon: -19.05 },
  { name: "Grímsvötn", lat: 64.42, lon: -17.33 }, { name: "Bárðarbunga", lat: 64.64, lon: -17.53 },
  { name: "Stromboli", lat: 38.79, lon: 15.21 }, { name: "Vulcano", lat: 38.4, lon: 14.96 },
  { name: "Teide", lat: 28.27, lon: -16.64 }, { name: "Cumbre Vieja", lat: 28.57, lon: -17.84 },
  { name: "Nyamuragira", lat: -1.41, lon: 29.2 }, { name: "Ol Doinyo Lengai", lat: -2.76, lon: 35.91 },
  { name: "Erta Ale", lat: 13.6, lon: 40.67 }, { name: "Nabro", lat: 13.37, lon: 41.7 },
  { name: "Dabbahu", lat: 12.6, lon: 40.48 }, { name: "Piton de la Fournaise", lat: -21.24, lon: 55.71 },
  { name: "Karthala", lat: -11.75, lon: 43.38 }, { name: "Fogo", lat: 14.95, lon: -24.35 },
  { name: "Nyiragongo II", lat: -1.5, lon: 29.24 }, { name: "Damavand", lat: 35.95, lon: 52.11 },
  { name: "Ararat", lat: 39.7, lon: 44.3 }, { name: "Elbrus", lat: 43.35, lon: 42.44 },
  { name: "Kliuchevskoi", lat: 56.06, lon: 160.64 }, { name: "Shiveluch", lat: 56.65, lon: 161.36 },
  { name: "Bezymianny", lat: 55.97, lon: 160.6 }, { name: "Tolbachik", lat: 55.83, lon: 160.33 },
  { name: "Karymsky", lat: 54.05, lon: 159.44 }, { name: "Avachinsky", lat: 53.26, lon: 158.83 },
  { name: "Ebeko", lat: 50.69, lon: 156.01 }, { name: "Sarychev", lat: 48.09, lon: 153.2 },
  { name: "Aso", lat: 32.88, lon: 131.1 }, { name: "Unzen", lat: 32.76, lon: 130.29 },
  { name: "Ontake", lat: 35.89, lon: 137.48 }, { name: "Asama", lat: 36.4, lon: 138.52 },
  { name: "Bandai", lat: 37.6, lon: 140.08 }, { name: "Usu", lat: 42.53, lon: 140.84 },
  { name: "Kelud", lat: -7.93, lon: 112.31 }, { name: "Bromo", lat: -7.94, lon: 112.95 },
  { name: "Semeru", lat: -8.11, lon: 112.92 }, { name: "Agung", lat: -8.34, lon: 115.51 },
  { name: "Rinjani", lat: -8.42, lon: 116.47 }, { name: "Tambora", lat: -8.25, lon: 118.0 },
  { name: "Sinabung", lat: 3.17, lon: 98.39 }, { name: "Kerinci", lat: -1.7, lon: 101.26 },
  { name: "Mayon", lat: 13.26, lon: 123.69 }, { name: "Kanlaon", lat: 10.41, lon: 123.13 },
  { name: "Bulusan", lat: 12.77, lon: 124.05 }, { name: "White Island", lat: -37.52, lon: 177.18 },
  { name: "Tongariro", lat: -39.13, lon: 175.64 }, { name: "Taupo", lat: -38.82, lon: 175.9 },
  { name: "Ambrym", lat: -16.25, lon: 168.12 }, { name: "Yasur", lat: -19.53, lon: 169.44 },
  { name: "Manam", lat: -4.08, lon: 145.04 }, { name: "Rabaul", lat: -4.27, lon: 152.2 },
  { name: "Ulawun", lat: -5.05, lon: 151.33 }, { name: "Bagana", lat: -6.14, lon: 155.2 },
  { name: "Soufrière Hills", lat: 16.72, lon: -62.18 }, { name: "Mount Pelée", lat: 14.81, lon: -61.17 },
  { name: "La Soufrière", lat: 13.33, lon: -61.18 }, { name: "Kick 'em Jenny", lat: 12.3, lon: -61.64 },
  { name: "Momotombo", lat: 12.42, lon: -86.54 }, { name: "Turrialba", lat: 10.03, lon: -83.77 },
];

// Angular distance (radians) between two lat/lon points on a unit sphere.
export function angularDistance(lat1, lon1, lat2, lon2) {
  const a = latLonToVector3(lat1, lon1, 1).normalize();
  const b = latLonToVector3(lat2, lon2, 1).normalize();
  return Math.acos(THREE.MathUtils.clamp(a.dot(b), -1, 1));
}

// Nearest active volcano within maxRad radians (~0.13 rad ≈ 7.5°). Returns { volcano, dist } or null.
export function nearestVolcano(lat, lon, maxRad = 0.13) {
  let best = null;
  for (const v of VOLCANOES) {
    const d = angularDistance(lat, lon, v.lat, v.lon);
    if (d <= maxRad && (!best || d < best.dist)) best = { volcano: v, dist: d };
  }
  return best;
}

// Volcanic eruption energy contribution (~1.2 magnitude units below the quake)
export function eruptionJoules(mw) {
  return energyJoules(Math.max(4, mw - 1.2));
}

// Fracture intensity grows exponentially with rupture duration (10-300s)
export function fractureFactor(durationSec) {
  return Math.pow(THREE.MathUtils.clamp((durationSec - 10) / (300 - 10), 0, 1), 2);
}
